
import html2markdown from '@notable/html2markdown';
import { cloneDeep } from 'lodash';
import { getCachedFeature } from '../cache/FeatureCache';
import { getTicketSettings } from '../cache/TicketSettingsCache';
import { platformService } from '../services/platform.service';
import { ARRANGED_SETTING_KEYS, LICENSE_FEATURE, SETTINGS_PAGES } from '../utils/constants';


const toObject = <T>(value: string) : T => {
    let objectParsed = {} as T;

    try {
        objectParsed =  JSON.parse(value) as T;
    } catch {
        objectParsed = {} as T;
    }

    return objectParsed;
};

export const hexToRgbA = (hex: string, opacity: string) => {
    var c;
    if(/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)){
        c= hex.substring(1).split('');
        if(c.length== 3){
            c= [c[0], c[0], c[1], c[1], c[2], c[2]];
        }
        c= '0x'+c.join('');
        return 'rgba('+[(c>>16)&255, (c>>8)&255, c&255].join(',')+`,${opacity})`;
    }
    throw new Error('Bad Hex');
}


export const CommonHelper = {
    toObject: toObject
}

export const getBodyHeight = (isInTeams: boolean) => {
    if (isInTeams) {
        return "calc(100vh - 52px)";
    } else {
        return "calc(100vh - 110px)"
    }
}

export const minifyHtmlString = (htmlString) => {
    let minified = htmlString.replace(/<!--[\s\S]*?-->/g, '');
    minified = minified.replace(/\s+/g, ' ');
    minified = minified.replace(/>\s+</g, '><');
    minified = minified.replace(/(\s*<[^>]+>)\s*([^<>\s]+)\s*(<[^>]+>\s*)/g, '$1$2$3');

    return minified.trim(); // Trim any remaining leading/trailing whitespace
}



export const getTicketTokenMentionList = async (props: MentionProps, reloadTicketPropertyList=false) => {
    if (reloadTicketPropertyList){
        const api = new platformService();
        const {data:{value}} = await api.getEntityProperties()
        props.ticketPropertyList = value;
    }
    else if (props.ticketPropertyList==null){
        props.ticketPropertyList=[];
    }

    // Offered only when time tracking is licensed AND the tenant has it on - both are folded
    // into the one flag by GetAvailableFeatures. With it off no time entry can exist, so the
    // token would resolve to "0m" in every message that used it. Read here rather than passed
    // in because both callers build their editor through this one function.
    const isTimeTrackingEnabled = await getCachedFeature(LICENSE_FEATURE.TimeTracking).catch(() => false);

    let suggestions = [
        { text: 'Requester First Name', value: '{Requester First Name}}', url: '{Requester.FirstName}}' },
        { text: 'Requester Last Name', value: '{Requester Last Name}}', url: '{Requester.LastName}}' },
        { text: 'Latest Comment', value: '{Latest Comment}}', url: '{LatestComment}}' },
        { text: 'Latest Commenter', value: '{Latest Commenter}}', url: '{LatestCommenter}}' },
        { text: 'Request', value: '{Request}}', url: '{Request}}' },
        { text: 'Ticket Id', value: '{Ticket Id}}', url: '{TicketId}}' },
        { text: 'Template Name', value: '{Template Name}}', url: '{TemplateName}}' },
        { text: 'Group', value: '{Group}}', url: '{Group}}' },
        { text: 'Resolution Note', value: '{Resolution Note}}', url: '{Note}}'},
        { text: 'Resolution Internal Note', value: '{Resolution Internal Note}}', url: '{InternalNote}}'},
        { text: 'Resolution Category', value: '{Resolution Category}}', url: '{ResolutionCategory}}'},
        { text: 'Resolution Draft KB', value: '{Resolution Draft KB}}', url: '{DrafKB}}'},
    ];

    // Listed explicitly rather than left to the ticketPropertyList loop below. That loop
    // substitutes the raw stored number ("183"); this resolves server-side through the
    // shared duration formatter, so the notification reads "3h and 3m" like the ticket and
    // the view column do. Named to match the view column, which is also "Total Agent Time".
    // Total Agent Time is the ticket's running total; the three below describe the change
    // that triggered the rule, so they resolve to empty on any rule not fired by a time
    // entry. Same guard, so all four disappear together with the licence or the tenant
    // toggle.
    if (isTimeTrackingEnabled) {
        suggestions.push({ text: 'Total Agent Time', value: '{Total Agent Time}}', url: '{TotalAgentTime}}' });
        suggestions.push({ text: 'Time Entry Duration', value: '{Time Entry Duration}}', url: '{TimeEntryDuration}}' });
        suggestions.push({ text: 'Time Entry Change', value: '{Time Entry Change}}', url: '{TimeEntryChange}}' });
        suggestions.push({ text: 'Time Entry Agent', value: '{Time Entry Agent}}', url: '{TimeEntryAgent}}' });
        // Date and start time are separate tokens rather than one combined "when", so a
        // message can use either alone. The date is always offered - every entry has one.
        suggestions.push({ text: 'Time Entry Date', value: '{Time Entry Date}}', url: '{TimeEntryDate}}' });

        // The start time is offered only when the tenant captures one. With that setting off
        // no entry carries a time, so the token would render empty in every message that used
        // it, and a token that can never resolve is worse than one that is absent. Read
        // inside this branch because with time tracking off the answer cannot matter.
        const { CaptureStartTime } = await getTicketSettings();

        if (CaptureStartTime) {
            suggestions.push({ text: 'Time Entry Start Time', value: '{Time Entry Start Time}}', url: '{TimeEntryStartTime}}' });
        }
    }

    if (props.additionSuggestions!=null){
        props.additionSuggestions.forEach((item:any)=>{
            suggestions.push({ text: item.text, value: `{${item.text}}}`, url: `{${item.value}}}` })
        });
    }

    if (props.ticketPropertyList!=null){
        props.ticketPropertyList.forEach((item:any) => {
            let text = item.replace(/([A-Z][a-z])/g,' $1').replace(/(\d)/g,' $1');
            if (text.startsWith(" ")) text = text.substring(1);
            suggestions.push({ text: text, value: `{${text}}}`, url: `{${item}}}` });
        });
    }

    suggestions.push({text: "Agent URL", value:'{Agent URL}}',url:'{AgentURL}}'});
    suggestions.push({text: "Requester URL", value:'{Requester URL}}',url:'{RequesterURL}}'});

    let mention = {
        separator: ' ',
        trigger: '{',
        suggestions: suggestions.sort((a, b) => a.text < b.text ? -1 : a.text > b.text ? 1 : 0)
    }

    return mention;

}

export const ConvertTokenValue = (suggestions: MensionSuggestion[], value:string) => {
    let temp = JSON.stringify(html2markdown(value));
    temp = temp.substring(1,temp.length-1);
    return temp;
}

export const GetArrangeSettingsKey = async () => {

    const [isResolutionNote,isNotificationCenter] = await Promise.all([
        getCachedFeature(LICENSE_FEATURE.ResolutionNote),
        getCachedFeature(LICENSE_FEATURE.NotificationCenter)
      ]);
    let excludeToSideNav = [];
    let cloneKeys = cloneDeep(ARRANGED_SETTING_KEYS);

    if (!isNotificationCenter)
    {
        let index = cloneKeys.findIndex(x=>x==SETTINGS_PAGES.NOTIFICATION_CENTER)
        if (index>=0){
            cloneKeys.splice(index,1,SETTINGS_PAGES.EMAIL_CONNECTOR);
        }
    }

    if (!isResolutionNote)
    {
        excludeToSideNav.push(SETTINGS_PAGES.RESOLUTIONCATEGORY);
    }
    return cloneKeys.filter(x=> !excludeToSideNav.includes(x));
}

export const FormatDropDownItems = (
    items: any[],
    selectedValue: number | null,
    valueProperty: string,
    returnName: string,
    returnValue: string,
    extraProperties: string[] = [],
    extraReturnProperties: any[] = []
  ) => {
    const formatted = [];

    let idProperty: string = "Id";
    let selectedItem: any = null;

    if (!items) items = [];

    items.forEach(i => {
      const f = {
        accessibilityitemprops: { id: i[idProperty] },
        header: i[valueProperty],
        selected: i.Id === selectedValue,
        ...i
      };

      extraProperties.forEach(p => { f[p.toLowerCase()] = i[p]; });
      formatted.push(f);

      if (f.selected) selectedItem = i;
    });

    const returnData = {
      [returnValue]: (selectedItem || { [valueProperty]: '' })[valueProperty],
      [returnName]: formatted
    };

    extraReturnProperties.forEach(r => {
        returnData[r.ReturnName] = selectedItem ? selectedItem[r.PropertyName] : r.DefaultValue;
    });

    return returnData;
  };